import axios, { type AxiosResponse } from 'axios'
import BaseService from '../BaseService'
import { require } from '@/utils/guards'
import type { Accommodation, AccommodationMainImage } from '@/types/models'

const translationsBaseUrl = `${import.meta.env.VITE_HOTEL_SEARCH_GRAPHQL_API_URL}`
const GRAPHQL_CLIENT_NAME = 'trivago-business-studio-property-details'

// ==================== GraphQL Queries ====================

const getFacilitiesTranslationsQuery = `
  query GetConceptDetails($input: ConceptDetailsInput!) {
    getConceptDetails(input: $input) {
      conceptDetails {
        nsid {
          ns
          id
        }
        translatedName {
          value
        }
      }
    }
  }
`

const getHotelChainsQuery = `
  query GetConceptsByNamespace($input: ConceptsByNamespaceInput!) {
    getConceptsByNamespace(input: $input) {
      namespace
      concepts {
        nsid {
          id
          ns
        }
        translatedName {
          value
        }
      }
    }
  }
`

// ==================== Type Definitions ====================

export interface NSID {
  id: number
  ns: number
}

export interface TranslatedName {
  value: string
}

export interface ConceptDetail {
  nsid: NSID
  translatedName: TranslatedName
}

export interface Concept {
  nsid: NSID
  translatedName: TranslatedName
}

export interface NamespaceResponse {
  namespace: string
  concepts: Concept[]
}

export interface SearchAccommodationRequest {
  searchTerm: string
  language?: string
}

export interface GetAccommodationsByIdsRequest {
  accommodationIds?: number[]
  organisationId: string
}

/** Partial update body for PUT /accommodations/{id}; omit a field to leave it unmodified, null to clear it where supported */
export interface AccommodationDTORequest {
  accommodationTypeId?: number | null
  chainId?: number | null
  propertyId?: string | null
  name?: string | null
  streetAddress?: string | null
  postalCode?: string | null
  city?: string | null
  phone?: string | null
  homepageUrl?: string | null
  email?: string | null
  latitude?: number | null
  longitude?: number | null
  starRating?: number | null
  checkInHour?: number | null
  checkOutHour?: number | null
}

export interface UpdatePropertyDetailsRequest {
  accommodationId: string
  body: AccommodationDTORequest
}

export interface AccommodationDescriptionDto {
  accommodationId: number
  languageCode: string
  source: string | null
  description: string | null
  modifiedAt: string | null
  isHotelierDescription: boolean
}

/** Raw feature shape from backend (no display name) */
export interface FeatureDto {
  featureId: number
  studioGroupId: number
}

/** Feature enriched with a translated display name (client-composed) */
export interface Feature extends FeatureDto {
  name?: string
}

/** Raw grouped-features shape from backend */
export interface GroupedFeatures {
  studioGroupId: number | null
  features: FeatureDto[]
}

/** Grouped features enriched with translated names (client-composed) */
export interface FeatureGroup {
  studioGroupId: number | null
  features: Feature[]
}

export interface AccommodationFeatureDto {
  accommodationId: number
  featureId: number
  isAvailable: boolean
  modifiedAt: string
}

export interface TranslationDto {
  accommodationId: number
  languageCode: string
  name: string
  streetAddress: string | null
  web: string | null
  modifiedAt: string | null
}

export interface CreateTranslationDto {
  languageCode: string
  name: string
  streetAddress?: string | null
  web?: string | null
}

export interface TranslationUpdateDto {
  name: string
  streetAddress?: string | null
  web?: string | null
}

export interface DeleteLanguageRequest {
  accommodationId: string
  language: string
}

export interface UpdateLanguageRequest {
  accommodationId: string
  language: string
  body: TranslationUpdateDto
}

export interface AddLanguageRequest {
  accommodationId: string
  body: CreateTranslationDto
}

export interface StatusDto {
  status: string
}

export interface SetMainImageRequest {
  accommodationId: string
  imageId: string
}

export interface UploadImageRequest {
  accommodationId: number
  body: FormData
}

/** Raw image shape from backend (no isMainImage/accommodationId — those don't exist on the wire) */
export interface AccommodationImageDto {
  imageId: string
  imagePath: string
  active: boolean
  width: number
  height: number
  extension: string
  mimeType: string
  setter: string
}

export interface AccommodationImagesListDto {
  images: AccommodationImageDto[]
}

export interface AccommodationImageUploadedResponseDto {
  image: AccommodationImageDto
}

export interface UpdateImageItemDto {
  image_id: string | null
  active: boolean
  reason: string
}

export interface UpdateImageRequestDto {
  updated: UpdateImageItemDto[]
}

export interface DeleteImageRequest {
  accommodationId: string
  body: UpdateImageRequestDto
}

/** Image data as composed client-side, combining the images list with the main-image marker */
export interface ImageData extends AccommodationImageDto {
  isMainImage: boolean
  accommodationId?: number
}

export interface VisitorProfileRequest {
  accommodationId: string
  startDate: string
}

export type SharesByKpi = Record<string, Record<string, number>>
export type SharesByLocale = Record<string, number>

// ==================== Service Class ====================

class AccommodationService extends BaseService {
  constructor() {
    super({ baseUrl: import.meta.env.VITE_NEW_ACCOMMODATIONS_API_URL })
  }

  newSearch({ searchTerm, language }: SearchAccommodationRequest): Promise<AxiosResponse> {
    return axios({
      url: `${this.baseUrl}/api/v1/accommodations/search/${searchTerm}`,
      method: 'GET',
      headers: {
        ...this.studioHeadersWithAttribution(),
        'x-trv-language': language || 'en-US',
      },
    })
  }

  getAccommodationsByListOfIds({
    accommodationIds = [],
    organisationId
  }: GetAccommodationsByIdsRequest): Promise<AxiosResponse<Accommodation[]>> {
    require(accommodationIds.length)
    require(organisationId)

    return axios({
      url: `${this.baseUrl}/api/v1/accommodations/${organisationId}/get`,
      method: 'POST',
      headers: this.studioHeadersWithAttribution(),
      maxBodyLength: Infinity,
      data: {
        ids: accommodationIds,
      },
    })
  }

  fetchNewAccommodationBasicInfo(accommodationId: string): Promise<AxiosResponse<Accommodation>> {
    return axios({
      url: `${this.baseUrl}/api/v1/accommodations/${accommodationId}`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  fetchPropertyDescriptions({ accommodationId }: { accommodationId: string }): Promise<AxiosResponse<AccommodationDescriptionDto[]>> {
    require(accommodationId)

    return axios({
      url: `${this.baseUrl}/api/v1/descriptions/${accommodationId}`,
      method: 'GET',
      headers: this.studioHeaders(),
    })
  }

  deletePropertyDescription({ accommodationId, languageCode }: { accommodationId: string; languageCode: string }): Promise<AxiosResponse<void>> {
    require(accommodationId)
    require(languageCode)

    return axios({
      url: `${this.baseUrl}/api/v1/descriptions/${accommodationId}/${languageCode}`,
      method: 'DELETE',
      headers: this.studioHeaders(),
    })
  }

  createPropertyDescription({ accommodationId, languageCode, body }: { accommodationId: string; languageCode: string; body: { description: string } }): Promise<AxiosResponse<AccommodationDescriptionDto>> {
    require(accommodationId)
    require(languageCode)
    require(body?.description)

    return axios({
      url: `${this.baseUrl}/api/v1/descriptions/${accommodationId}/${languageCode}`,
      method: 'POST',
      headers: this.studioHeaders(),
      data: body,
    })
  }

  updatePropertyDescription({ accommodationId, languageCode, body }: { accommodationId: string; languageCode: string; body: { description: string } }): Promise<AxiosResponse<AccommodationDescriptionDto>> {
    require(accommodationId)
    require(languageCode)
    require(body?.description)

    return axios({
      url: `${this.baseUrl}/api/v1/descriptions/${accommodationId}/${languageCode}`,
      method: 'PUT',
      headers: this.studioHeaders(),
      data: body,
    })
  }

  async fetchListTranslations(
    languageCode: string,
    namespace: string[]
  ): Promise<AxiosResponse<NamespaceResponse>> {
    const response = await axios({
      url: `${translationsBaseUrl}`,
      method: 'POST',
      data: {
        query: getHotelChainsQuery,
        variables: {
          input: {
            namespaces: namespace,
          },
        },
      },
      headers: this.graphqlHeaders(languageCode, GRAPHQL_CLIENT_NAME),
    })

    response.data = response.data.data.getConceptsByNamespace
    return response
  }

  updatePropertyDetails({
    accommodationId,
    body
  }: UpdatePropertyDetailsRequest): Promise<AxiosResponse<Accommodation>> {
    return axios({
      url: `${this.baseUrl}/api/v1/accommodations/${accommodationId}`,
      method: 'PUT',
      data: body,
      headers: this.studioHeadersWithAttribution(),
    })
  }

  updatePropertyFeatures(
    accommodationId: string,
    groupId: string,
    body: FeatureDto[]
  ): Promise<AxiosResponse<AccommodationFeatureDto[]>> {
    return axios({
      url: `${this.baseUrl}/api/v1/accommodations/${accommodationId}/features/by-group/${groupId}`,
      method: 'PUT',
      data: body,
      headers: this.studioHeadersWithAttribution(),
    })
  }

  fetchLanguagesFields({ accommodationId }: { accommodationId: string }): Promise<AxiosResponse<TranslationDto[]>> {
    return axios({
      url: `${this.baseUrl}/api/v1/translations/${accommodationId}`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  deleteLanguage({ accommodationId, language }: DeleteLanguageRequest): Promise<AxiosResponse<void>> {
    return axios({
      url: `${this.baseUrl}/api/v1/translations/${accommodationId}/${language}`,
      method: 'DELETE',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  updateLanguage({
    accommodationId,
    language,
    body
  }: UpdateLanguageRequest): Promise<AxiosResponse<TranslationDto | null>> {
    return axios({
      url: `${this.baseUrl}/api/v1/translations/${accommodationId}/${language}`,
      method: 'PUT',
      data: body,
      headers: this.studioHeadersWithAttribution(),
    })
  }

  addNewLanguage({ accommodationId, body }: AddLanguageRequest): Promise<AxiosResponse<TranslationDto | null>> {
    return axios({
      url: `${this.baseUrl}/api/v1/translations/${accommodationId}`,
      method: 'POST',
      data: body,
      headers: this.studioHeadersWithAttribution(),
    })
  }

  getMainImage(accommodationId: string): Promise<AxiosResponse<AccommodationMainImage>> {
    return axios({
      url: `${this.baseUrl}/api/v1/images/${accommodationId}/main-images`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  async setNewMainImage({ accommodationId, imageId }: SetMainImageRequest): Promise<AxiosResponse<StatusDto>> {
    return await axios({
      url: `${this.baseUrl}/api/v1/images/${accommodationId}/set-main-image`,
      method: 'POST',
      data: {
        imageId,
      },
      headers: this.studioHeadersWithAttribution(),
    })
  }

  async uploadImage({ accommodationId, body }: UploadImageRequest): Promise<AxiosResponse<AccommodationImageUploadedResponseDto>> {
    return await axios({
      url: `${this.baseUrl}/api/v1/images/${accommodationId}/upload`,
      method: 'POST',
      data: body,
      headers: {
        ...this.studioHeadersWithAttribution(),
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  deleteImage({ accommodationId, body }: DeleteImageRequest): Promise<AxiosResponse<UpdateImageRequestDto>> {
    return axios({
      url: `${this.baseUrl}/api/v1/images/${accommodationId}/images`,
      method: 'PUT',
      data: body,
      headers: this.studioHeadersWithAttribution(),
    })
  }

  async fetchImages(accommodationId: string): Promise<ImageData[]> {
    return await axios<AccommodationImagesListDto>({
      url: `${this.baseUrl}/api/v1/images/${accommodationId}`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    }).then(async response => {
      const result: ImageData[] = response.data.images.map(item => ({ isMainImage: false, ...item }))
      let images: ImageData[] = []
      await this.getMainImage(accommodationId)
        .then(mainImageData => {
          if (mainImageData.data) {
            images = [
              {
                ...mainImageData.data,
                active: true,
                isMainImage: true,
                imageId: `mainImage-${mainImageData.data.imageId}`,
              },
              ...result,
            ]
          }
        })
        .catch(() => {
          images = result
        })
      return images
    })
  }

  async fetchFields(fieldGroupId: string, locale: string): Promise<Feature[]> {
    return await axios<FeatureDto[]>({
      url: `${this.baseUrl}/api/v1/features/by-group/${fieldGroupId}`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    }).then(async response => {
      const featuresIds: number[] = []
      response.data.map(item => featuresIds.push(item.featureId))

      const featuresNames = await this.fetchFacilitiesTranslations(locale, featuresIds)

      return response.data.map(item => {
        const translatedName = featuresNames.data.find(
          (featuresName: ConceptDetail) => featuresName.nsid.id === item.featureId
        )
        return { ...item, name: translatedName?.translatedName?.value }
      })
    })
  }

  async fetchAccommodationFields(accommodationId: string, locale: string): Promise<FeatureGroup[]> {
    return await axios<GroupedFeatures[]>({
      url: `${this.baseUrl}/api/v1/accommodations/${accommodationId}/features/by-group`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    }).then(async response => {
      const featuresIds: number[] = []
      response.data.map(item => {
        item.features.forEach(feature => featuresIds.push(feature.featureId))
      })

      const featuresNames = await this.fetchFacilitiesTranslations(locale, featuresIds)

      return response.data.map(item => {
        const newFields = item.features.map(feature => {
          const translatedName = featuresNames.data.find(
            (featuresName: ConceptDetail) => featuresName.nsid.id === feature.featureId
          )
          return { ...feature, name: translatedName?.translatedName?.value }
        })
        return {
          ...item,
          features: newFields,
        }
      })
    })
  }

  async fetchFacilitiesTranslations(
    locale: string,
    featuresIds: number[]
  ): Promise<AxiosResponse<ConceptDetail[]>> {
    const input = featuresIds?.map(item => {
      return { ns: 300, id: item }
    })

    const response = await axios({
      url: `${translationsBaseUrl}`,
      method: 'POST',
      data: {
        query: getFacilitiesTranslationsQuery,
        variables: {
          input: { nsids: input },
        },
      },
      headers: this.graphqlHeaders(locale, GRAPHQL_CLIENT_NAME),
    })

    response.data = response.data.data.getConceptDetails.conceptDetails
    return response
  }

  fetchTimeFramesTrends({ accommodationId, startDate }: VisitorProfileRequest): Promise<AxiosResponse<SharesByKpi>> {
    require(accommodationId)
    require(startDate)

    return axios({
      url: `${this.baseUrl}/api/v1/accommodations/${accommodationId}/trend/traveler/type?startDate=${startDate}`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  fetchVisitorsProfileDuration({ accommodationId, startDate }: VisitorProfileRequest): Promise<AxiosResponse<SharesByKpi>> {
    require(accommodationId)
    require(startDate)

    return axios({
      url: `${this.baseUrl}/api/v1/accommodations/${accommodationId}/trend/traveler/stay?startDate=${startDate}`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  fetchVisitorsProfileCountries({ accommodationId, startDate }: VisitorProfileRequest): Promise<AxiosResponse<SharesByLocale>> {
    require(accommodationId)
    require(startDate)

    return axios({
      url: `${this.baseUrl}/api/v1/accommodations/${accommodationId}/trend/traveler/locale?startDate=${startDate}`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    })
  }
}

export default new AccommodationService()
